import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: 'About MeowCentral - Our Story & Team',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
  description: 'Learn about MeowCentral, the ultimate cat community. Meet our team of cat lovers, veterinary advisors, and content creators dedicated to celebrating everything feline.',
  mainEntity: {
    '@type': 'Organization',
    name: 'MeowCentral',
    foundingDate: '2020',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 4 },
    description: 'The largest online cat community featuring videos, adoption resources, expert care guides, and premium cat products.',
    member: [
      { '@type': 'Person', name: 'Sarah Johnson', jobTitle: 'Founder & Cat Enthusiast' },
      { '@type': 'Person', name: 'Mike Chen', jobTitle: 'Content Creator' },
      { '@type': 'Person', name: 'Emma Davis', jobTitle: 'Veterinary Advisor' },
      { '@type': 'Person', name: 'Alex Rodriguez', jobTitle: 'Community Manager' },
    ],
  },
};

export default function AboutPage() {
  const team = [
    {
      name: 'Sarah Johnson',
      role: 'Founder & Cat Enthusiast',
      image: 'https://public.readdy.ai/ai/img_res/e1acfbd62536ba97b1392c89908fc8c8.jpg',
      bio: 'Cat lover for 20+ years with a passion for creating the ultimate cat community.',
    },
    {
      name: 'Mike Chen',
      role: 'Content Creator',
      image: 'https://readdy.ai/api/search-image?query=young%20man%20holding%20camera%20with%20cat%2C%20creative%20professional%2C%20studio%20setting%2C%20friendly%20smile%2C%20modern%20photography%20equipment%2C%20artistic%20portrait&width=300&height=300&seq=team2&orientation=squarish',
      bio: 'Capturing the cutest cat moments and sharing them with the world.',
    },
    {
      name: 'Emma Davis',
      role: 'Veterinary Advisor',
      image: 'https://readdy.ai/api/search-image?query=female%20veterinarian%20with%20cat%2C%20professional%20medical%20setting%2C%20caring%20expression%2C%20white%20coat%2C%20gentle%20interaction%20with%20pet%2C%20trustworthy%20portrait&width=300&height=300&seq=team3&orientation=squarish',
      bio: 'Certified vet ensuring all our content promotes healthy and happy cats.',
    },
    {
      name: 'Alex Rodriguez',
      role: 'Community Manager',
      image: 'https://readdy.ai/api/search-image?query=friendly%20man%20with%20multiple%20cats%2C%20warm%20smile%2C%20cozy%20home%20environment%2C%20casual%20professional%20attire%2C%20welcoming%20atmosphere%2C%20natural%20portrait&width=300&height=300&seq=team4&orientation=squarish',
      bio: 'Building connections between cat lovers worldwide.',
    },
  ];

  const stats = [
    { number: '1M+', label: 'Cat Videos', icon: 'ri-play-circle-fill' },
    { number: '500K+', label: 'Happy Members', icon: 'ri-user-heart-fill' },
    { number: '10K+', label: 'Products', icon: 'ri-shopping-bag-fill' },
    { number: '50K+', label: 'Daily Visitors', icon: 'ri-eye-fill' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <SEOHead
        title="About MeowCentral - Our Story, Team & Cat-Loving Mission"
        description="Meet the passionate team behind MeowCentral — cat lovers, vets, and creators building the world's greatest cat community with 1M+ videos, 500K+ members, and 10K+ products."
        keywords="about MeowCentral, cat community team, cat website founders, cat lovers community"
        canonical="/about"
        schema={aboutSchema}
      />
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-20 pb-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
            About Our Purrfect Community 🐱
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We're on a mission to create the ultimate destination for cat lovers worldwide. 
            From adorable videos to premium products, we celebrate everything feline!
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow"
              >
                <i className={`${stat.icon} text-5xl text-blue-500 mb-4`}></i>
                <div className="text-4xl font-bold text-gray-800 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Story Section */}
      <div className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-12 flex flex-col justify-center">
                <h2 className="text-4xl font-bold text-gray-800 mb-6">Our Story</h2>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  It all started with a simple love for cats and a dream to create a space where 
                  cat enthusiasts could come together. What began as a small collection of cute 
                  kitten videos has grown into the largest cat community on the internet.
                </p>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Today, we're proud to serve millions of cat lovers with endless entertainment, 
                  expert advice, and premium products. Every purr, every meow, and every adorable 
                  moment brings us closer to our mission.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Join us in celebrating the joy that cats bring to our lives, one video at a time!
                </p>
              </div>
              <div className="relative h-96 md:h-auto">
                <img
                  src="https://readdy.ai/api/search-image?query=multiple%20adorable%20kittens%20playing%20together%2C%20colorful%20toys%2C%20bright%20cheerful%20setting%2C%20playful%20atmosphere%2C%20group%20of%20cute%20cats%2C%20joyful%20scene%2C%20high%20quality%20pet%20photography&width=600&height=800&seq=story1&orientation=portrait"
                  alt="Our Story"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Team Section */}
      <div className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600">
              Passionate cat lovers dedicated to bringing you the best content
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-blue-500 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="px-4 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-3-fill text-3xl text-blue-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Love for Cats</h3>
              <p className="text-gray-600">
                Every decision we make is driven by our genuine love and care for cats and their wellbeing.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-team-fill text-3xl text-purple-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Community First</h3>
              <p className="text-gray-600">
                Building a supportive, inclusive community where all cat lovers feel welcome and valued.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-star-fill text-3xl text-pink-500"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Quality Content</h3>
              <p className="text-gray-600">
                Curating only the best, most adorable, and entertaining cat content for our community.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}